import axios from 'axios';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { updatedEntities, denormalisedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { apiBaseUrl } from '../../util/api';
import { types as sdkTypes } from '../../util/sdkLoader';
// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 42;
const { UUID } = sdkTypes;
// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/ManageListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/ManageListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/ManageListingsPage/FETCH_LISTINGS_ERROR';

export const FETCH_All_LISTINGS_REQUEST = 'app/ManageListingsPage/FETCH_All_LISTINGS_REQUEST';
export const FETCH_All_LISTINGS_SUCCESS = 'app/ManageListingsPage/FETCH_All_LISTINGS_SUCCESS';
export const FETCH_All_LISTINGS_ERROR = 'app/ManageListingsPage/FETCH_All_LISTINGS_ERROR';

export const OPEN_LISTING_REQUEST = 'app/ManageListingsPage/OPEN_LISTING_REQUEST';
export const OPEN_LISTING_SUCCESS = 'app/ManageListingsPage/OPEN_LISTING_SUCCESS';
export const OPEN_LISTING_ERROR = 'app/ManageListingsPage/OPEN_LISTING_ERROR';

export const CLOSE_LISTING_REQUEST = 'app/ManageListingsPage/CLOSE_LISTING_REQUEST';
export const CLOSE_LISTING_SUCCESS = 'app/ManageListingsPage/CLOSE_LISTING_SUCCESS';
export const CLOSE_LISTING_ERROR = 'app/ManageListingsPage/CLOSE_LISTING_ERROR';

export const FETCH_FAV_COUNT_SUCCESS = 'app/ManageListingsPage/FETCH_FAV_COUNT_SUCCESS';

export const ADD_OWN_ENTITIES = 'app/ManageListingsPage/ADD_OWN_ENTITIES';

export const SPINNER_COUNT_REQUEST = 'app/ManageListingsPage/SPINNER_COUNT_REQUEST';
export const SPINNER_COUNT_SUCCESS = 'app/ManageListingsPage/SPINNER_COUNT_SUCCESS';

export const LISTING_PAGE_VIEW = 'app/ManageListingsPage/LISTING_PAGE_VIEW';
export const LISTING_REVIEW_COUNT = 'app/ManageListingsPage/LISTING_REVIEW_COUNT';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  currentPageResultIds: [],
  ownEntities: {},
  openingListing: null,
  openingListingError: null,
  closingListing: null,
  closingListingError: null,
  totalListing: null,
  favCount: null,
  countInProgress: false,
  listingViewCounter: null,
  listingReviewCounter: null,
};

const resultIds = data => data.data.map(l => l.id);

const merge = (state, sdkResponse) => {
  const apiResponse = sdkResponse.data;
  return {
    ...state,
    ownEntities: updatedEntities({ ...state.ownEntities }, apiResponse),
  };
};

const updateListingAttributes = (state, listingEntity) => {
  const oldListing = state.ownEntities.ownListing[listingEntity.id.uuid];
  const updatedListing = { ...oldListing, attributes: listingEntity.attributes };
  const ownListingEntities = {
    ...state.ownEntities.ownListing,
    [listingEntity.id.uuid]: updatedListing,
  };
  return {
    ...state,
    ownEntities: { ...state.ownEntities, ownListing: ownListingEntities },
  };
};

const manageListingsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        queryInProgress: false,
      };
    case FETCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryInProgress: false, queryListingsError: payload };

    case OPEN_LISTING_REQUEST:
      return {
        ...state,
        openingListing: payload.listingId,
        openingListingError: null,
      };
    case OPEN_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        openingListing: null,
      };
    case OPEN_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        openingListing: null,
        openingListingError: {
          listingId: state.openingListing,
          error: payload,
        },
      };
    }

    case CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closingListing: payload.listingId,
        closingListingError: null,
      };
    case CLOSE_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        closingListing: null,
      };
    case CLOSE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        closingListing: null,
        closingListingError: {
          listingId: state.closingListing,
          error: payload,
        },
      };
    }

    case ADD_OWN_ENTITIES:
      return merge(state, payload);
    case FETCH_All_LISTINGS_REQUEST:
      return {
        ...state,
        queryInProgress: true,
        totalListing: null,
        listingViewCounter: null,
        listingReviewCounter: null,
      };
    case FETCH_All_LISTINGS_SUCCESS:
      return {
        ...state,
        queryInProgress: false,
        totalListing: payload,
      };
    case FETCH_All_LISTINGS_ERROR:
      console.error(payload);
      return {
        ...state,
        queryInProgress: false,
      };
    case FETCH_FAV_COUNT_SUCCESS:
      return {
        ...state,
        favCount: payload,
      };
    case SPINNER_COUNT_REQUEST:
      return { ...state, countInProgress: true, queryInProgress: true };
    case SPINNER_COUNT_SUCCESS:
      return { ...state, countInProgress: false, queryInProgress: false };
    case LISTING_PAGE_VIEW:
      return {
        ...state,
        listingViewCounter: payload,
      };
    case LISTING_REVIEW_COUNT: {
      return {
        ...state,
        listingReviewCounter: payload,
      };
    }
    default:
      return state;
  }
};

export default manageListingsPageReducer;

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ManageListingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Action creators ================ //

// This works the same way as addMarketplaceEntities,
// but we don't want to mix own listings with searched listings
// (own listings data contains different info - e.g. exact location etc.)
export const addOwnEntities = sdkResponse => ({
  type: ADD_OWN_ENTITIES,
  payload: sdkResponse,
});

export const openListingRequest = listingId => ({
  type: OPEN_LISTING_REQUEST,
  payload: { listingId },
});

export const openListingSuccess = response => ({
  type: OPEN_LISTING_SUCCESS,
  payload: response.data,
});

export const openListingError = e => ({
  type: OPEN_LISTING_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = listingId => ({
  type: CLOSE_LISTING_REQUEST,
  payload: { listingId },
});

export const closeListingSuccess = response => ({
  type: CLOSE_LISTING_SUCCESS,
  payload: response.data,
});

export const closeListingError = e => ({
  type: CLOSE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = queryParams => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
});

export const queryListingsSuccess = response => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const queryListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const queryAllListingsRequest = () => ({
  type: FETCH_All_LISTINGS_REQUEST,
});
export const queryAllListingsSuccess = response => ({
  type: FETCH_All_LISTINGS_SUCCESS,
  payload: response,
});
export const queryAllListingsError = e => ({
  type: FETCH_All_LISTINGS_ERROR,
  error: true,
  payload: e,
});
export const fetchFavCount = res => ({
  type: FETCH_FAV_COUNT_SUCCESS,
  payload: res,
});
export const fetchCountRequest = () => ({
  type: SPINNER_COUNT_REQUEST,
});
export const fetchCountSuccess = () => ({
  type: SPINNER_COUNT_SUCCESS,
});

export const fetchListingViewCounter = res => ({
  type: LISTING_PAGE_VIEW,
  payload: res,
});
export const fetchListingReViewCounter = res => ({
  type: LISTING_REVIEW_COUNT,
  payload: res,
});

// Throwing error for new (loadData may need that info)
export const queryOwnListings = queryParams => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(queryParams));

  const { perPage, ...rest } = queryParams;
  const params = { ...rest, perPage };

  return sdk.ownListings
    .query(params)
    .then(response => {
      dispatch(addOwnEntities(response));
      dispatch(queryListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};

export const closeListing = listingId => (dispatch, getState, sdk) => {
  dispatch(fetchCountRequest());
  dispatch(closeListingRequest(listingId));

  return sdk.ownListings
    .close({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(closeListingSuccess(response));
      return response;
    })
    .then(() => {
      return dispatch(updateFavListing({ listingId: listingId.uuid, active: false }));
    })
    .then(() => {
      return dispatch(queryListing());
    })
    .then(() => {
      dispatch(fetchCountSuccess());
    })
    .catch(e => {
      dispatch(closeListingError(storableError(e)));
    });
};

export const openListing = listingId => (dispatch, getState, sdk) => {
  dispatch(fetchCountRequest());
  dispatch(openListingRequest(listingId));

  return sdk.ownListings
    .open({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(openListingSuccess(response));
      return response;
    })
    .then(res => {
      return dispatch(updateFavListing({ listingId: listingId.uuid, active: true }));
    })
    .then(() => {
      return dispatch(queryListing());
    })
    .then(() => {
      dispatch(fetchCountSuccess());
    })
    .catch(e => {
      dispatch(openListingError(storableError(e)));
    });
};
export const queryListing = () => async (dispatch, getState, sdk) => {
  dispatch(queryAllListingsRequest());
  await dispatch(fetchCurrentUser());
  const fetchedcurrentUser = getState().user.currentUser;
  const currentUserID = fetchedcurrentUser.id.uuid;
  return (
    sdk.listings
      .query({
        authorId: currentUserID,
        // perPage: 1,
      })
      .then(res => {
        dispatch(queryAllListingsSuccess(res?.data?.meta?.totalItems));
        return res;
      })
      .then(async res => {
        let pageCount = [];
        let listingIds = [];
        pageCount = res?.data?.data?.map(m => m?.attributes?.metadata?.pageCount);
        listingIds = res?.data?.data?.map(m => m.id.uuid);
        const totalPages = res?.data?.meta?.totalPages;
        if (totalPages > 1)
          for (let i = 2; i <= totalPages; i++) {
            const listingData = await sdk.listings.query({
              authorId: currentUserID,
              page: i,
              // perPage: 1,
            });
            const additionalPageCount = listingData?.data?.data?.map(
              m => m?.attributes?.metadata?.pageCount
            );
            const additionalListingIds = listingData?.data?.data?.map(m => m.id.uuid);
            pageCount = pageCount.concat(additionalPageCount);
            listingIds = listingIds.concat(additionalListingIds);
          }
        return { pageCount, listingIds };
      })
      .then(res => {
        const { pageCount, listingIds } = res;
        const totalListingViewCount =
          pageCount !== undefined && pageCount.length > 0 && pageCount.filter(f=>f).reduce((a, b) => a + b, 0);
        dispatch(fetchListingViewCounter(totalListingViewCount));
        return { listingIds };
      })

      //for review count
      .then(async res => {
        const { listingIds } = res;
        const reviewdata = await Promise.all(
          listingIds.map(listingId => {
            return sdk.reviews.query({
              listingId,
            });
          })
        );
        return reviewdata;
      })
      .then(reviewdata => {
        const totalReviewArray = reviewdata.map(m => m?.data?.meta?.totalItems);
        const totalReviewCount =
          totalReviewArray !== undefined &&
          totalReviewArray.length > 0 &&
          totalReviewArray.filter(f=>f).reduce((a, b) => a + b, 0);

        dispatch(fetchListingReViewCounter(totalReviewCount));
      })
      .catch(e => {
        dispatch(queryAllListingsError(storableError(e)));
        throw e;
      })
  );
};

export const updateFavListing = params => async (dispatch, getState, sdk) => {
  const { listingId, active } = params;
  const url = `${apiBaseUrl()}/api/favourite/updatefav`;
  const body = {
    // listingIds: [listingId],
    listingId,
    active,
  };
  return axios.post(url, body, { withCredentials: true }).then(() => {
    return dispatch(queryFavCount());
  });
};

export const queryFavCount = () => async (dispatch, getState, sdk) => {
  const url = `${apiBaseUrl()}/api/favourite/getAuthorFavListing`;
  return axios
    .get(url, { withCredentials: true })
    .then(res => {
      dispatch(fetchFavCount(res?.data));
    })
    .catch(e => {
      console.log(e);
    });
};

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return Promise.all([
    dispatch(
      queryOwnListings({
        ...queryParams,
        page,
        perPage: RESULT_PAGE_SIZE,
        include: ['images', 'currentStock'],
        'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        'limit.images': 1,
      })
    ),
    dispatch(queryListing()),
    dispatch(queryFavCount()),
  ]);
};
